import { getTrackingProductId } from '@features/products/utils'
import { centAmountToAmountV2 } from '@lib/format-price'
import { CartLineItemsAddInput } from '@lib/graphql/graphql'
import { MutationConfig } from '@lib/react-query'
import { getCurrencyFromCountry } from '@lib/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mapEdgesToItems } from '@utils/map'
import {
  CurrencyCodes,
  getTrackingProductUrl,
  pushAddToBasketTrackingEvent,
} from '@utils/tracking-utils'
import { CHANNEL } from 'config'
import { useSelector } from 'react-redux'
import { selectAlpha2Code, selectChannel } from '@redux/globalSlice'
import { cartsQueryKeys } from '../query-keys'
import {
  findOrCreateCart,
  getCartIdFromCookies,
  saveCartIdToCookie,
} from '../utils'
import { cartAddLineItems } from './cart-add-line-items'

export async function cartAddMultipleLineItems(
  lineItems: CartLineItemsAddInput['lineItems'],
  channel: CHANNEL,
  currency: string,
  countryCode: string,
) {
  const cart = await findOrCreateCart({
    cartId: getCartIdFromCookies(),
    channel,
    currency,
    countryCode,
  })

  if (!cart) {
    console.error('Ups, something went wrong')
    return
  }

  saveCartIdToCookie(cart.id)

  const addCartLineItemsResponse = await cartAddLineItems({
    cartId: cart.id,
    lineItems,
  })

  return {
    cart: addCartLineItemsResponse.cartLineItemsAdd.cart,
    variantIds: lineItems.map((lineItem) => lineItem.variantId),
  }
}

export const useCartAddMultipleLineItems = (
  mutationConfig?: MutationConfig<typeof cartAddMultipleLineItems>,
) => {
  const country = useSelector(selectAlpha2Code)
  const channel = useSelector(selectChannel)
  const queryClient = useQueryClient()
  const { onSuccess, ...restConfig } = mutationConfig || {}
  const currency = getCurrencyFromCountry(country)

  return useMutation({
    mutationFn: (lineItemsInput: CartLineItemsAddInput['lineItems']) =>
      cartAddMultipleLineItems(lineItemsInput, channel, currency, country),
    onSuccess: (data, variables, context) => {
      if (!data?.cart) {
        onSuccess?.(data, variables, context)
        return
      }

      const cart = data.cart
      queryClient.setQueryData(cartsQueryKeys.all, cart)

      const cartLineItems = mapEdgesToItems(cart.lineItems) ?? []
      const quantityMap = new Map<string, number>()
      variables.forEach(({ variantId, quantity }) =>
        quantityMap.set(variantId, quantity),
      )

      const products = data.variantIds
        .map((variantId) => {
          const lineItem = cartLineItems.find(
            (item) => item.variant?.id === variantId,
          )

          if (!lineItem?.variant) return null

          const requestedQuantity = quantityMap.get(variantId) ?? lineItem.quantity

          return {
            id: getTrackingProductId(
              lineItem.variant.product.id,
              lineItem.variant.product.metadata,
            ),
            name: lineItem.variant.product.name,
            variant: lineItem.variant.name,
            category: lineItem.variant.product.tags
              ?.find((tag) => tag.startsWith('type_'))
              ?.split('_')[1]
              .replace(/-/g, ''),
            brand: lineItem.variant.product.vendor,
            image_url: lineItem.variant.image?.src,
            url: getTrackingProductUrl(
              lineItem.variant.product.slug,
              lineItem.variant.sku,
            ),
            quantity: requestedQuantity,
            price: centAmountToAmountV2(
              lineItem.unitPrice.discountedPrice
                ? lineItem.unitPrice.discountedPrice.value
                : lineItem.unitPrice.value,
            )?.toString(),
            dimension1: lineItem.variant.sku,
          }
        })
        .filter((product): product is NonNullable<typeof product> => Boolean(product))

      if (products.length) {
        pushAddToBasketTrackingEvent({
          currencyCode: cart.currency as CurrencyCodes,
          products,
          cart,
        })
      }

      onSuccess?.(data, variables, context)
    },
    ...restConfig,
  })
}
